import { useState, useEffect, useRef, useContext } from "react";
import { useParams } from "react-router-dom";
import FullCalendar from "@fullcalendar/react";
// import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import ptLocale from "@fullcalendar/core/locales/pt";

import { AuthContext } from "../../../../shared/context/auth-context";
import { ClinicaContext } from "../../../../shared/context/clinica-context";
import { useHttpClient } from "../../../../shared/hooks/http-hook";

import TooltipViewOnly from "./componentsFullCallendar/TooltipViewOnly";
import "./Datatable.scss";

import { useLoading } from "../../../../shared/context/LoadingContext";
import toast from "react-hot-toast";

function DatatableViewOnly({ medicos }) {
  const { startLoading, stopLoading } = useLoading();

  const [events, setEvents] = useState([]);
  const [tooltipEvent, setTooltipEvent] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [duracaoConsulta, setduracaoConsulta] = useState(30);
  const [horarioAtendimento, setHorarioAtendimento] = useState({
    start: "08:00:00",
    end: "20:00:00",
  });

  const [loadOldAppointments, setLoadOldAppointments] = useState(false);
  const [showLoadOldButton, setShowLoadOldButton] = useState(false);

  const clickRef = useRef(null);
  const tooltipRef = useRef(null);
  const calendarRef = useRef(null);
  const previousRangeRef = useRef(null);
  const savedDateRef = useRef(null);

  const { sendRequest } = useHttpClient();

  const auth = useContext(AuthContext);
  const clinica = useContext(ClinicaContext);
  const medicoId = useParams().medicoId;

  useEffect(() => {
    setEvents([]);
    const fetchData = async () => {
      try {
        //Duracao de tratamentoIsolado e horario de atendimento
        if (medicos) {
          let temp = medicos.filter((m) => m.id === medicoId);

          if (temp[0].duracaoConsulta)
            setduracaoConsulta(temp[0].duracaoConsulta);

          if (temp[0].horarioAtendimento)
            setHorarioAtendimento(temp[0].horarioAtendimento);
        } else {
          const response = await sendRequest(
            `${process.env.REACT_APP_BACKEND_LINK}/api/users/${medicoId}`,
            "GET",
            null,
            {
              Authorization: "Bearer " + auth.token,
            }
          );
          if (response.user.horarioAtendimento)
            setHorarioAtendimento(response.user.horarioAtendimento);
          if (response.user.duracaoConsulta)
            setduracaoConsulta(response.user.duracaoConsulta);
        }

        startLoading();
        const formData = new FormData();
        formData.append("medicoId", medicoId);

        const [
          tratamentoIsoladosResponse,
          agendaResponse,
          compromissosResponse,
          agendamentosEmTransicaoResponse,
        ] = await Promise.all([
          sendRequest(
            `${process.env.REACT_APP_BACKEND_LINK}/api/tratamentoIsolados/agenda/${clinica.clinica._id}`,
            "PATCH",
            formData,
            { Authorization: "Bearer " + auth.token }
          ),
          sendRequest(
            `${process.env.REACT_APP_BACKEND_LINK}/api/agenda/${medicoId}`,
            "GET",
            null,
            { Authorization: "Bearer " + auth.token }
          ),
          sendRequest(
            `${process.env.REACT_APP_BACKEND_LINK}/api/compromissos/${medicoId}`,
            "GET",
            null,
            { Authorization: "Bearer " + auth.token }
          ),
          sendRequest(
            `${process.env.REACT_APP_BACKEND_LINK}/api/agendamentosemtransicao/${medicoId}`,
            "GET",
            null,
            { Authorization: "Bearer " + auth.token }
          ),
        ]);

        const agendaMarcacoes = agendaResponse.agenda || [];
        const compromissosMarcacoes = compromissosResponse.compromissos || [];
        const tratamentoIsoladosMarcacoes =
          tratamentoIsoladosResponse.tratamentoIsolados || [];
        const agendamentosEmTransicaoMarcacoes =
          agendamentosEmTransicaoResponse.agenda || [];

        const formattedEvents = [
          ...formatAppointments(agendaMarcacoes),
          ...formatCompromissos(compromissosMarcacoes),
          ...formatTransitionAppointments(agendamentosEmTransicaoMarcacoes),
          ...formatTratamentoIsolados(tratamentoIsoladosMarcacoes),
        ];

        setEvents(formattedEvents);
      } catch (err) {
        console.error(err);
      } finally {
        stopLoading();
      }
    };

    fetchData();
  }, [sendRequest, auth.token, clinica.clinica._id, medicos, medicoId]);

  // Fetch separado para agendamentos antigos
  useEffect(() => {
    if (!loadOldAppointments) return;

    const fetchOldAppointments = async () => {
      try {
        startLoading();
        const formData = new FormData();
        formData.append("medicoId", medicoId);

        const [
          tratamentoIsoladosResponse,
          agendaResponse,
          compromissosResponse,
        ] = await Promise.all([
          sendRequest(
            `${process.env.REACT_APP_BACKEND_LINK}/api/tratamentoIsolados/agenda/${clinica.clinica._id}?loadOld=true`,
            "PATCH",
            formData,
            { Authorization: "Bearer " + auth.token }
          ),
          sendRequest(
            `${process.env.REACT_APP_BACKEND_LINK}/api/agenda/${medicoId}?loadOld=true`,
            "GET",
            null,
            { Authorization: "Bearer " + auth.token }
          ),
          sendRequest(
            `${process.env.REACT_APP_BACKEND_LINK}/api/compromissos/${medicoId}?loadOld=true`,
            "GET",
            null,
            { Authorization: "Bearer " + auth.token }
          ),
        ]);

        const oldEvents = [
          ...formatAppointments(agendaResponse.agenda || []),
          ...formatCompromissos(compromissosResponse.compromissos || []),
          ...formatTratamentoIsolados(
            tratamentoIsoladosResponse.tratamentoIsolados || []
          ),
        ];

        // Merge sem duplicados
        setEvents((prev) => {
          const existingIds = new Set(prev.map((e) => e.id));
          const newEvents = oldEvents.filter((e) => !existingIds.has(e.id));
          return [...prev, ...newEvents];
        });
      } catch (err) {
        console.error(err);
      } finally {
        stopLoading();
      }
    };

    fetchOldAppointments();
  }, [loadOldAppointments]);

  const handleDatesSet = (dateInfo) => {
    const startTime = dateInfo.start.getTime();

    // Só processa se a data inicial mudou significativamente
    if (previousRangeRef.current !== startTime) {
      previousRangeRef.current = startTime;

      const oneMonthAgo = new Date();
      oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

      // Só mostra o botão se não carregou ainda e está vendo datas antigas
      if (dateInfo.start < oneMonthAgo && !loadOldAppointments) {
        setShowLoadOldButton(true);
      } else if (dateInfo.start >= oneMonthAgo) {
        setShowLoadOldButton(false);
      }
    }
  };

  // Função para carregar agendamentos antigos
  const handleLoadOldAppointments = () => {
    // Salva a data atual do calendário antes de recarregar
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      savedDateRef.current = calendarApi.getDate();
    }

    setLoadOldAppointments(true);
    setShowLoadOldButton(false);
    toast.success("Carregando agendamentos antigos...");
  };

  useEffect(() => {
    if (loadOldAppointments && savedDateRef.current && calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.gotoDate(savedDateRef.current);
    }
  }, [events]);

  const formatAppointments = (appointments) => {
    return appointments.map((appointment) => ({
      id: appointment._id,
      title: ` ${appointment.cliente.nome.split(" ")[0]} ${
        appointment.cliente.nome.split(" ")[
          appointment.cliente.nome.split(" ").length - 1
        ]
      } - ${
        appointment.avaliacao
          ? "Avaliação"
          : appointment.categoriaTratamento?.categoria
      }`,
      start: new Date(appointment.data_inicio_execucao),
      end: new Date(appointment.data_fim_execucao),

      backgroundColor: appointment.atendido
        ? "#F2FAF5"
        : appointment.faltou
        ? "#FFF3F6"
        : appointment.avaliacao
        ? "#fde6c5"
        : appointment.orcamento
        ? "#E0F7FA" // verde-água claro
        : "#ecf6ff",

      borderColor: appointment.atendido
        ? "#39B93A"
        : appointment.faltou
        ? "#D96C60"
        : appointment.avaliacao
        ? "#F57C00"
        : appointment.orcamento
        ? "#0097A7" // verde-água forte
        : "#1976D2",

      textColor: appointment.atendido
        ? "#39B93A"
        : appointment.faltou
        ? "#D96C60"
        : appointment.avaliacao
        ? " #F57C00"
        : appointment.orcamento
        ? "#0097A7" // verde-água forte
        : "#1976D2",

      extendedProps: {
        clientId: appointment.cliente._id,
        procedureId: appointment.categoriaTratamento?._id,
        isEvaluation: appointment.avaliacao,
        isAttended: appointment.atendido,
        isMissed: appointment.faltou,
        inTransition: false,
        isCompromisso: false,
        planoId: appointment.planoTratamento,
        orcamentoId: appointment.orcamento, // NOVO: adicionar ID do orçamento
        isOrcamento: !!appointment.orcamento, // NOVO: flag para identificar orçamento
        agendadoPor: appointment.agendadoPor?.name,
        observacoesAgendamento: appointment.observacoesAgendamento,
      },
    }));
  };

  const formatCompromissos = (compromissos) => {
    return compromissos.map((compromisso) => ({
      id: compromisso._id,
      title: " " + compromisso.compromisso,
      start: new Date(compromisso.data_inicio_execucao),
      end: new Date(compromisso.data_fim_execucao),
      backgroundColor: "#F5F5F5",
      borderColor: "#616161",
      textColor: "#616161",
      extendedProps: {
        isCompromisso: true,
        agendadoPor: compromisso.agendadoPor?.name,
      },
    }));
  };

  const formatTratamentoIsolados = (trtIsol) => {
    return trtIsol.map((tratamentoIsolado) => ({
      id: tratamentoIsolado._id,
      title: ` ${tratamentoIsolado.cliente.nome.split(" ")[0]} ${
        tratamentoIsolado.cliente.nome.split(" ")[
          tratamentoIsolado.cliente.nome.split(" ").length - 1
        ]
      } - ${
        tratamentoIsolado.tratamento?.designacao
          ? tratamentoIsolado.tratamento.designacao
          : tratamentoIsolado.categoriaTratamento?.categoria
      }`,
      start: new Date(tratamentoIsolado.data_inicio_execucao),
      end: new Date(tratamentoIsolado.data_fim_execucao),
      backgroundColor: tratamentoIsolado.atendido
        ? "#F2FAF5"
        : tratamentoIsolado.faltou
        ? "#FFF3F6"
        : "#D7CCC8",
      borderColor: tratamentoIsolado.atendido
        ? "#39B93A"
        : tratamentoIsolado.faltou
        ? "#D96C60"
        : "#5D4037",
      textColor: tratamentoIsolado.atendido
        ? "#39B93A"
        : tratamentoIsolado.faltou
        ? "#D96C60"
        : "#5D4037",
      extendedProps: {
        isTratamentoIsolado: true,
        clientId: tratamentoIsolado.cliente._id,
        isAttended: tratamentoIsolado.atendido,
        isMissed: tratamentoIsolado.faltou,
        agendadoPor: tratamentoIsolado.agendadoPor?.name,
        observacoesAgendamento: tratamentoIsolado.observacoesAgendamento,
      },
    }));
  };

  const formatTransitionAppointments = (appointments) => {
    return appointments.map((appointment) => ({
      id: appointment._id,
      title: ` ${appointment.cliente.nome.split(" ")[0]} ${
        appointment.cliente.nome.split(" ")[
          appointment.cliente.nome.split(" ").length - 1
        ]
      } - ${appointment.categoriaTratamento.categoria}`,
      start: new Date(appointment.data_inicio_execucao),
      end: new Date(appointment.data_fim_execucao),
      backgroundColor: appointment.atendido
        ? "#F2FAF5"
        : appointment.faltou
        ? "#FFF3F6"
        : "#ebe5f5",
      borderColor: appointment.atendido
        ? "#39B93A"
        : appointment.faltou
        ? "#D96C60"
        : "#512DA8",
      textColor: appointment.atendido
        ? "#39B93A"
        : appointment.faltou
        ? "#D96C60"
        : "#512DA8",
      extendedProps: {
        clientId: appointment.cliente._id,
        procedureId: appointment.categoriaTratamento._id,
        inTransition: true,
        isAttended: appointment.atendido,
        isMissed: appointment.faltou,
        agendadoPor: appointment.agendadoPor?.name,
        observacoesAgendamento: appointment.observacoesAgendamento,
      },
    }));
  };

  const handleDateClick = (arg) => {
    if (clickRef.current && arg.date.getTime() === clickRef.current.getTime()) {
      clickRef.current = null;
    } else {
      // First click
      clickRef.current = arg.date;
      setTimeout(() => {
        clickRef.current = null;
      }, 300);
    }
  };

  const handleEventClick = (info) => {
    if (
      clickRef.current &&
      info.event.start.getTime() === clickRef.current.getTime()
    ) {
      clickRef.current = null;
    } else {
      // First click - show tooltip
      const rect = info.el.getBoundingClientRect();
      setTooltipPosition({
        x: rect.left + window.scrollX,
        y: rect.bottom + window.scrollY,
      });
      setTooltipEvent(info.event);

      // Set up for potential double click
      clickRef.current = info.event.start;
      setTimeout(() => {
        clickRef.current = null;
      }, 300);
    }
  };

  const handleCloseTooltip = () => {
    setTooltipEvent(null);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
        handleCloseTooltip();
      }
    };

    if (tooltipEvent) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [tooltipEvent]);

  return (
    <div className="datatable-container" style={{ backgroundColor: "white" }}>
      {showLoadOldButton && !loadOldAppointments && (
        <div
          style={{
            padding: "16px",
            backgroundColor: "#FFF3E0",
            borderRadius: "8px",
            marginBottom: "16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            border: "1px solid #FFB74D",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{ fontSize: "20px" }}>📅</span>
            <span style={{ color: "#E65100", fontWeight: "500" }}>
              Você está visualizando datas antigas. Deseja carregar os
              agendamentos anteriores?
            </span>
          </div>
          <button
            onClick={handleLoadOldAppointments}
            style={{
              padding: "8px 16px",
              backgroundColor: "#FF9800",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontWeight: "500",
              whiteSpace: "nowrap",
            }}
          >
            Carregar Agendamentos Antigos
          </button>
        </div>
      )}
      {events && (
        <FullCalendar
          ref={calendarRef}
          plugins={[timeGridPlugin, interactionPlugin]}
          // plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="timeGridWeek"
          locale={ptLocale}
          headerToolbar={{
            left: "prev,next today",
            center: "title",
            right: "timeGridWeek,timeGridDay",
            // right: "dayGridMonth,timeGridWeek,timeGridDay",
          }}
          buttonText={{
            today: "Esta Semana",
            // month: "Mês",
            week: "Semana",
            day: "Dia",
          }}
          height="auto"
          slotDuration={`00:${duracaoConsulta}:00`}
          slotLabelInterval={`00:${duracaoConsulta}`}
          slotLabelFormat={{
            hour: "numeric",
            minute: "2-digit",
            omitZeroMinute: false,
            meridiem: "short",
          }}
          slotMinTime={horarioAtendimento.start}
          slotMaxTime={horarioAtendimento.end}
          allDaySlot={false}
          events={events}
          dateClick={handleDateClick}
          eventClick={handleEventClick}
          eventContent={renderEventContent}
          datesSet={handleDatesSet}
          hiddenDays={[0]}
          firstDay={1}
          editable={false}
        />
      )}
      {tooltipEvent && (
        <div
          ref={tooltipRef}
          style={{
            position: "absolute",
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y}px`,
          }}
        >
          <TooltipViewOnly event={tooltipEvent} />
        </div>
      )}
    </div>
  );
}

function renderEventContent(eventInfo) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        width: "100%",
      }}
    >
      <div>
        <b>{eventInfo.timeText}</b>
        <i>{eventInfo.event.title}</i>
      </div>
      {eventInfo.event.extendedProps.observacoesAgendamento && (
        <div
          style={{
            color: "#F57C00",
            fontSize: "11px",
            fontWeight: "bold",
            marginLeft: "4px",
            alignSelf: "flex-start",
          }}
        >
          Obs
        </div>
      )}
    </div>
  );
}

export default DatatableViewOnly;
